import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Alert,
  CircularProgress,
  Snackbar,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import type { DataSourceHealth } from '../../types/dataHealth.types';
import type { IntegrationStatus } from '../../services/siteIntegrationService';
import { initiateOAuthFlow } from '../../utils/oauthPopupHelper';

interface ConnectionStatus {
  connected: boolean;
  siteId: number;
  expiresAt?: string;
  message?: string;
}

interface ApiResponse<T> {
  data: T;
  success: boolean;
  requestId: string;
  timestamp: number;
}

interface GSCSetupPanelProps {
  health?: DataSourceHealth;
  isLoading: boolean;
  siteId?: number;
  siteName?: string;
  /** Pre-fetched integration status from unified endpoint (optional) */
  integrationStatus?: IntegrationStatus;
}

export const GSCSetupPanel: React.FC<GSCSetupPanelProps> = ({ health, isLoading, siteId, siteName, integrationStatus }) => {
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus | null>(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

  // Listen for OAuth callback messages from popup
  useEffect(() => {
    const handleOAuthCallback = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === 'oauth-callback' && event.data?.provider === 'google') {
        if (event.data.status === 'success') {
          setSnackbar({ open: true, message: 'Google Search Console connected successfully!', severity: 'success' });
          // Refresh connection status after brief delay
          setTimeout(() => window.location.reload(), 1500);
        } else {
          setSnackbar({ open: true, message: event.data.message || 'OAuth connection failed', severity: 'error' });
        }
      }
    };
    window.addEventListener('message', handleOAuthCallback);
    return () => window.removeEventListener('message', handleOAuthCallback);
  }, []);

  // Use integrationStatus if provided (from unified endpoint), else derive from health status
  useEffect(() => {
    // If we have pre-fetched integration status, use it directly
    if (integrationStatus) {
      const status = {
        connected: integrationStatus.connected,
        siteId: siteId || 0,
        expiresAt: integrationStatus.expiresAt,
        message: integrationStatus.message,
      };
      setConnectionStatus(status);
      
      // Trigger event if GSC is already configured on page load
      const isExpired = integrationStatus.expiresAt 
        ? new Date(integrationStatus.expiresAt) < new Date()
        : integrationStatus.message?.toLowerCase().includes('expired') || false;
      
      if (integrationStatus.connected && !isExpired) {
        // Dispatch custom event for site connected
        const event = new CustomEvent('gsc-site-connected', {
          detail: {
            siteId: siteId,
            siteName: siteName,
            expiresAt: integrationStatus.expiresAt,
            timestamp: new Date().toISOString(),
          }
        });
        window.dispatchEvent(event);
        console.log('[GSCSetupPanel] GSC already configured - triggered gsc-site-connected event', {
          siteId,
          siteName,
          expiresAt: integrationStatus.expiresAt,
        });
      }
      return;
    }

    // Derive connection status from health data instead of making API call
    // If health status is HEALTHY, assume OAuth is connected
    if (health) {
      const isHealthy = health.status === 'HEALTHY';
      const status = {
        connected: isHealthy,
        siteId: siteId || 0,
        message: isHealthy ? 'Connected' : 'Not connected or needs authentication',
      };
      setConnectionStatus(status);
      
      // Trigger event if GSC is healthy on page load
      if (isHealthy) {
        const event = new CustomEvent('gsc-site-connected', {
          detail: {
            siteId: siteId,
            siteName: siteName,
            timestamp: new Date().toISOString(),
          }
        });
        window.dispatchEvent(event);
        console.log('[GSCSetupPanel] GSC configured (via health) - triggered gsc-site-connected event', {
          siteId,
          siteName,
        });
      }
    } else {
      // No health data yet
      setConnectionStatus({
        connected: false,
        siteId: siteId || 0,
        message: 'Not configured',
      });
    }
  }, [siteId, siteName, integrationStatus, health]);

  if (isLoading || statusLoading) {
    return (
      <Box sx={{ px: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  const isConfigured = connectionStatus?.connected || false;
  
  // Check if token is expired by comparing expiresAt with current time or checking message
  const isExpired = connectionStatus?.expiresAt 
    ? new Date(connectionStatus.expiresAt) < new Date()
    : connectionStatus?.message?.toLowerCase().includes('expired') || false;
  
  // Connection is valid only if connected AND not expired
  const isValidConnection = isConfigured && !isExpired;

  return (
    <Box sx={{ px: 3 }}>
      <Card variant="outlined">
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
            <Box>
              <Typography variant="h5" gutterBottom>
                Google Search Console Integration
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Connect your GSC property to import search performance data, query analytics, and page metrics{siteName ? ` for ${siteName}` : ''}.
              </Typography>
            </Box>
            <Chip
              label={isValidConnection ? 'Configured' : isExpired ? 'Token Expired' : 'Not Configured'}
              color={isValidConnection ? 'success' : isExpired ? 'warning' : 'default'}
              icon={isValidConnection ? <CheckIcon /> : isExpired ? <ScheduleIcon /> : <ErrorIcon />}
            />
          </Box>

          <Box my={3}>
            <Typography variant="subtitle2" gutterBottom>
              What you'll get:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <CheckIcon color="success" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Search query performance data (clicks, impressions, CTR, position)" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckIcon color="success" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Page-level SEO metrics and trends" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckIcon color="success" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Index coverage and crawl statistics" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckIcon color="success" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Automated daily data synchronization" />
              </ListItem>
            </List>
          </Box>

          <Alert severity={isValidConnection ? 'success' : isExpired ? 'warning' : 'info'} sx={{ mb: 2 }}>
            {isValidConnection ? (
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  ✓ GSC Connected for this site
                </Typography>
                {connectionStatus?.expiresAt && (
                  <Typography variant="caption">
                    Token expires: {new Date(connectionStatus.expiresAt).toLocaleString()}
                  </Typography>
                )}
                {health && (
                  <Typography variant="caption" display="block">
                    Last Data Sync: {health.lastSuccessfulSync ? new Date(health.lastSuccessfulSync).toLocaleString() : 'Never'}
                  </Typography>
                )}
              </Box>
            ) : isExpired ? (
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  ⚠ GSC Token Expired - Reconnection Required
                </Typography>
                <Typography variant="caption" display="block">
                  {connectionStatus?.message || 'Your OAuth token has expired. Please reconnect to continue syncing data.'}
                </Typography>
                {connectionStatus?.expiresAt && (
                  <Typography variant="caption" display="block">
                    Token expired: {new Date(connectionStatus.expiresAt).toLocaleString()}
                  </Typography>
                )}
              </Box>
            ) : (
              <Typography variant="body2">
                GSC is not connected for this site. Click "Connect This Site" to authenticate and start collecting data.
              </Typography>
            )}
          </Alert>

          <Box display="flex" gap={2} flexWrap="wrap">
            {isConfigured && (
              <Button
                variant={isExpired ? 'contained' : 'outlined'}
                color={isExpired ? 'warning' : 'primary'}
                startIcon={<LinkIcon />}
                onClick={() => siteId && initiateOAuthFlow('gsc', siteId)}
              >
                {isExpired ? 'Reconnect Now' : 'Reconnect / Refresh Token'}
              </Button>
            )}
            {!isConfigured && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<LinkIcon />}
                onClick={() => siteId && initiateOAuthFlow('gsc', siteId)}
              >
                Connect This Site
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
    </Box>
  );
};
